import { useBootstrap } from './tenant-config-module/use-tenant-config.js';
import { BootstrapError } from './ui-module/bootstrap-error.js';
import { BootstrapSkeleton } from './ui-module/bootstrap-skeleton.js';
import { MockApp, type MockAppProps } from './dev-module/mock-app.js';
import {
  CancelRoutes,
  type PinnedAppProps,
} from './cancel-module/routing-feature/cancel-routes.js';
import type { PinningVerifier, NowFn } from './cancel-module/routing-feature/cancel-routes.type.js';

interface AppProps {
  /** Test-Hook: überschreibt den Default-`verifyPinning` im Pinning-Bootstrap. */
  readonly verifyPinning?: PinningVerifier;
  /** Test-Hook: fixiert die Wall-Clock für die 24-h-Cutoff-Logik. */
  readonly now?: NowFn;
}

/**
 * Liefert das Vite-Mock-Flag. Liest `import.meta.env.VITE_MOCK_MODE` und
 * akzeptiert den Wert sowohl als String `"true"` als auch als Boolean.
 */
export function isMockModeEnv(): boolean {
  const raw: unknown = import.meta.env.VITE_MOCK_MODE;
  return raw === true || raw === 'true';
}

/**
 * Wurzelkomponente: liest den Bootstrap-Status, rendert je nach Status den
 * passenden Inhalt. Bei `ready` übernimmt `<PinnedApp>` (Pinning-Gate +
 * State-Machine + Token-Routing).
 */
export function App(props: AppProps = {}): JSX.Element {
  const bootstrap = useBootstrap();

  if (bootstrap.status === 'error') {
    return <BootstrapError message={bootstrap.message} />;
  }
  if (bootstrap.status !== 'ready') {
    return <BootstrapSkeleton />;
  }
  if (isMockModeEnv()) {
    const mockProps: MockAppProps = {
      config: bootstrap.config,
      ...(props.now ? { now: props.now } : {}),
    };
    return <MockApp {...mockProps} />;
  }
  const pinnedAppProps: PinnedAppProps = {
    config: bootstrap.config,
    ...(props.verifyPinning ? { verifyPinning: props.verifyPinning } : {}),
    ...(props.now ? { now: props.now } : {}),
  };
  return <CancelRoutes {...pinnedAppProps} />;
}
